import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Check,
  Copy,
  FileText,
  Instagram,
  Mail,
  MessageCircle,
  MessagesSquare,
  Plus,
  Trash2,
} from "lucide-react";
import { useStore } from "../lib/store";
import { isDemoMode } from "../lib/supabase";
import { showFeedback } from "../lib/feedback";
import {
  COMMUNICATION_CHANNELS,
  type CommunicationChannel,
  type MessageTemplate,
  type MessageTemplateVersion,
} from "../features/communication/contracts";
import { PageHead } from "../ui/PageHead";
import { Button } from "../ui/Button";
import { IconButton } from "../ui/IconButton";
import { SearchInput } from "../ui/SearchInput";
import { Dialog } from "../ui/Dialog";
import { Field, TextArea, TextInput } from "../ui/Field";
import { Select } from "../ui/Select";
import { ToolbarSpacer } from "../ui/PageHead";
import { ConfirmDialog } from "./PerfilDialogs";
import "./modelos.css";

/**
 * Biblioteca de modelos de mensagem.
 *
 * O protótipo dá aos modelos uma página própria — canal à esquerda, lista no
 * meio, prévia à direita — porque escolher um texto para reutilizar é uma
 * tarefa de cadastro, não algo para fazer dentro do compositor. O diálogo de
 * "Modelos" da conversa continua existindo para inserir; aqui se cria, edita,
 * duplica e arquiva.
 *
 * O domínio guarda rascunho editável e versão publicada imutável (§27.3). A
 * biblioteca fala em "salvar": cada salvamento publica uma versão nova e é ela
 * que a conversa passa a oferecer. Versões anteriores continuam intactas.
 */

const CHANNEL_LABEL: Record<CommunicationChannel, string> = {
  whatsapp: "WhatsApp",
  instagram: "Instagram",
  messenger: "Messenger",
  email: "E-mail",
};

function channelIcon(channel: CommunicationChannel) {
  if (channel === "whatsapp") return <MessageCircle aria-hidden="true" />;
  if (channel === "instagram") return <Instagram aria-hidden="true" />;
  if (channel === "messenger") return <MessagesSquare aria-hidden="true" />;
  return <Mail aria-hidden="true" />;
}

/** Rótulo humano da variável: a chave é do domínio, a capitalização é de tela. */
function variableLabel(name: string): string {
  return name.charAt(0).toLocaleUpperCase("pt-BR") + name.slice(1);
}

/**
 * As variáveis na ordem em que aparecem no texto.
 *
 * O domínio guarda a lista ordenada alfabeticamente, o que é bom para comparar
 * versões e ruim para ler: "Empresa e Nome" numa mensagem que começa com o
 * nome soa trocado.
 */
function variablesInReadingOrder(variables: readonly string[], text: string): string[] {
  const position = (name: string) => {
    const index = text.toLowerCase().indexOf(`{{${name.toLowerCase()}`);
    return index < 0 ? Number.MAX_SAFE_INTEGER : index;
  };
  return [...variables].sort((left, right) => position(left) - position(right));
}

/** Quebra o texto em pedaços para destacar a variável sem montar HTML. */
function highlight(text: string) {
  const parts: Array<{ text: string; variable: boolean }> = [];
  let index = 0;
  for (const match of text.matchAll(/\{\{\s*[\w.-]+\s*\}\}/g)) {
    const start = match.index ?? 0;
    if (start > index) parts.push({ text: text.slice(index, start), variable: false });
    parts.push({ text: match[0], variable: true });
    index = start + match[0].length;
  }
  if (index < text.length) parts.push({ text: text.slice(index), variable: false });
  return parts;
}

interface LibraryRow {
  template: MessageTemplate;
  /** Versão publicada mais recente; o rascunho só aparece se nada foi publicado. */
  version?: MessageTemplateVersion;
}

type ChannelFilter = "all" | CommunicationChannel;

/** Ordem do protótipo: conversa primeiro, e-mail por último. */
const CHANNEL_ORDER: CommunicationChannel[] = ["whatsapp", "instagram", "messenger", "email"];

export function ModelosPage() {
  const store = useStore();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ template?: MessageTemplate; version?: MessageTemplateVersion } | null>(null);
  const [removing, setRemoving] = useState<MessageTemplate | null>(null);
  const [busy, setBusy] = useState(false);

  const channelParam = params.get("canal");
  const channel: ChannelFilter =
    channelParam && (COMMUNICATION_CHANNELS as readonly string[]).includes(channelParam)
      ? (channelParam as CommunicationChannel)
      : "all";

  const templates = useMemo(
    () => (store.messageTemplates ?? []).filter((template) => !template.archived),
    [store.messageTemplates],
  );
  const allVersions = store.messageTemplateVersions;

  const rows: LibraryRow[] = useMemo(() => {
    const versions = allVersions ?? [];
    const pick = (templateId: string) => {
      const own = versions.filter((version) => version.templateId === templateId);
      const published = own
        .filter((version) => version.status === "published")
        .sort((left, right) => right.version - left.version)[0];
      return published ?? own.sort((left, right) => right.version - left.version)[0];
    };
    return templates
      .map((template) => ({ template, version: pick(template.id) }))
      .sort((left, right) => left.template.name.localeCompare(right.template.name, "pt-BR"));
  }, [templates, allVersions]);

  const term = query.trim().toLocaleLowerCase("pt-BR");
  const filtered = rows.filter((row) => {
    if (channel !== "all" && row.template.channel !== channel) return false;
    if (!term) return true;
    return (
      row.template.name.toLocaleLowerCase("pt-BR").includes(term) ||
      (row.version?.bodyText ?? "").toLocaleLowerCase("pt-BR").includes(term)
    );
  });

  const selected =
    filtered.find((row) => row.template.id === selectedId) ?? filtered[0] ?? undefined;

  const setChannel = (next: ChannelFilter) => {
    const nextParams = new URLSearchParams(params);
    if (next === "all") nextParams.delete("canal");
    else nextParams.set("canal", next);
    setParams(nextParams, { replace: true });
  };

  // Fora do demo os comandos de modelo recusam por falta de persistência; o
  // controle fica visível e desabilitado com o motivo, em vez de falhar depois
  // que a pessoa já escreveu (§18).
  const writable = isDemoMode;
  const blockedReason = "Os modelos de mensagem ainda não têm persistência neste ambiente.";

  const save = async (input: {
    templateId?: string;
    name: string;
    channel: CommunicationChannel;
    bodyText: string;
  }) => {
    setBusy(true);
    try {
      // Canal de conversa não tem assunto; o domínio exige um, então ele
      // acompanha o nome do modelo e só o e-mail o mostra na edição.
      const subject = input.channel === "email" ? input.name : input.name;
      const result = input.templateId
        ? await store.saveMessageTemplateDraft({
            templateId: input.templateId,
            name: input.name,
            subject,
            bodyText: input.bodyText,
          })
        : await store.createMessageTemplate({
            channel: input.channel,
            name: input.name,
            subject,
            bodyText: input.bodyText,
          });
      if (!result.ok) return false;
      const draft = result.value.version;
      if (draft && draft.status === "draft") {
        const published = await store.publishMessageTemplateVersion({ versionId: draft.id });
        if (!published.ok) return false;
      }
      setSelectedId(result.value.template.id);
      showFeedback(input.templateId ? "Modelo atualizado." : "Modelo criado.", "success");
      return true;
    } finally {
      setBusy(false);
    }
  };

  const duplicate = async (row: LibraryRow) => {
    if (!row.version) return;
    await save({
      name: `${row.template.name} (cópia)`,
      channel: row.template.channel,
      bodyText: row.version.bodyText,
    });
  };

  const counts = (value: ChannelFilter) =>
    value === "all" ? rows.length : rows.filter((row) => row.template.channel === value).length;

  return (
    <div className="cx-modelos">
      <PageHead
        title="Modelos de mensagem"
        sub="Suas respostas organizadas, prontas para personalizar."
        actions={
          <Button
            variant="primary"
            icon={<Plus size={15} aria-hidden="true" />}
            disabled={!writable}
            title={writable ? undefined : blockedReason}
            onClick={() => setEditing({})}
          >
            Novo modelo
          </Button>
        }
      />

      <div className="cl-shell">
        <nav className="cl-side" aria-label="Canais dos modelos">
          <span className="cl-side-label">Biblioteca</span>
          {(["all", ...CHANNEL_ORDER] as ChannelFilter[]).map((item) => (
            <button
              key={item}
              type="button"
              className="cl-channel"
              aria-pressed={channel === item}
              onClick={() => setChannel(item)}
            >
              {item === "all" ? <FileText aria-hidden="true" /> : channelIcon(item)}
              <span>{item === "all" ? "Todos os modelos" : CHANNEL_LABEL[item]}</span>
              <b className="num">{counts(item)}</b>
            </button>
          ))}
          <p className="cl-side-note">
            Uma biblioteca para suas mensagens e e-mails. O canal ajuda a organizar cada resposta.
          </p>
        </nav>

        <section className="cl-workspace" aria-label="Biblioteca de modelos">
          <div className="cl-toolbar">
            <SearchInput
              value={query}
              onChange={setQuery}
              label="Buscar modelos"
              placeholder="Buscar por nome ou mensagem…"
            />
            <span className="cl-count" aria-live="polite">
              {filtered.length} de {rows.length} {rows.length === 1 ? "modelo" : "modelos"}
            </span>
          </div>

          <div className="cl-columns">
            <section className="cl-results" aria-label="Modelos encontrados">
              <div className="cl-section-label">
                <span>{channel === "all" ? "Todos os modelos" : CHANNEL_LABEL[channel]}</span>
                <span>Mensagem</span>
              </div>
              {filtered.map((row) => {
                const variables = row.version?.variables ?? [];
                return (
                  <button
                    key={row.template.id}
                    type="button"
                    className="cl-row"
                    aria-pressed={row.template.id === selected?.template.id}
                    onClick={() => setSelectedId(row.template.id)}
                  >
                    <span className="cl-row-head">
                      <span className="cl-channel-mark" aria-hidden="true">
                        {channelIcon(row.template.channel)}
                      </span>
                      <strong>{row.template.name}</strong>
                      {row.template.id === selected?.template.id && (
                        <Check size={16} aria-hidden="true" />
                      )}
                    </span>
                    <span className="cl-row-text">{row.version?.bodyText ?? "Sem texto publicado."}</span>
                    <span className="cl-row-meta">
                      <span>{CHANNEL_LABEL[row.template.channel]}</span>
                      <span aria-hidden="true">·</span>
                      <span>
                        {variables.length} {variables.length === 1 ? "variável" : "variáveis"}
                      </span>
                    </span>
                  </button>
                );
              })}
              {filtered.length === 0 && (
                <div className="cl-empty">
                  <FileText size={28} aria-hidden="true" />
                  <h3>{rows.length ? "Nenhum modelo encontrado" : "Sua próxima resposta começa aqui"}</h3>
                  <p>
                    {rows.length
                      ? "Ajuste o termo ou explore outro canal."
                      : "Crie seu primeiro modelo e personalize a mensagem em cada conversa."}
                  </p>
                  {rows.length ? (
                    <Button
                      onClick={() => {
                        setQuery("");
                        setChannel("all");
                      }}
                    >
                      Limpar filtros
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      icon={<Plus size={15} aria-hidden="true" />}
                      disabled={!writable}
                      title={writable ? undefined : blockedReason}
                      onClick={() => setEditing({})}
                    >
                      Criar modelo
                    </Button>
                  )}
                </div>
              )}
            </section>

            <section className="cl-preview" aria-label="Prévia do modelo selecionado">
              {selected?.version ? (
                <>
                  <div className="cl-preview-top">
                    <span>Prévia do modelo</span>
                    <span aria-hidden="true">{channelIcon(selected.template.channel)}</span>
                  </div>
                  <h2>{selected.template.name}</h2>
                  <p className="cl-preview-sub">
                    {CHANNEL_LABEL[selected.template.channel]} · Texto reutilizável
                  </p>
                  <div className="cl-message">
                    {highlight(selected.version.bodyText).map((part, index) =>
                      part.variable ? <mark key={index}>{part.text}</mark> : <span key={index}>{part.text}</span>,
                    )}
                  </div>
                  <p className="cl-preview-hint">
                    {selected.version.variables.length
                      ? "As variáveis destacadas são preenchidas com os dados do contato ao inserir na conversa."
                      : "O texto fica disponível para revisão antes de enviar."}
                  </p>
                  <dl className="cl-details">
                    <div>
                      <dt>Canal</dt>
                      <dd>{CHANNEL_LABEL[selected.template.channel]}</dd>
                    </div>
                    <div>
                      <dt>Personalização</dt>
                      <dd>
                        {variablesInReadingOrder(selected.version.variables, selected.version.bodyText)
                          .map(variableLabel)
                          .join(" e ") || "Sem variáveis"}
                      </dd>
                    </div>
                    <div>
                      <dt>Extensão</dt>
                      <dd>{selected.version.bodyText.length} caracteres</dd>
                    </div>
                  </dl>
                  <p className="cl-preview-hint">
                    Para inserir, abra uma conversa e escolha este texto em <strong>Modelos</strong>.
                  </p>
                  <div className="cl-preview-actions">
                    <Button
                      variant="primary"
                      icon={<FileText size={15} aria-hidden="true" />}
                      disabled={!writable}
                      title={writable ? undefined : blockedReason}
                      onClick={() => setEditing({ template: selected.template, version: selected.version })}
                    >
                      Editar modelo
                    </Button>
                    <IconButton
                      label="Duplicar modelo"
                      icon={<Copy size={15} />}
                      disabled={!writable || busy}
                      title={writable ? undefined : blockedReason}
                      onClick={() => void duplicate(selected)}
                    />
                    <IconButton
                      label="Excluir modelo"
                      icon={<Trash2 size={15} />}
                      disabled={!writable}
                      title={writable ? undefined : blockedReason}
                      onClick={() => setRemoving(selected.template)}
                    />
                  </div>
                </>
              ) : (
                <div className="cl-empty">
                  <MessagesSquare size={28} aria-hidden="true" />
                  <h3>Uma prévia antes de usar</h3>
                  <p>Selecione um modelo para conferir o texto e suas variáveis.</p>
                </div>
              )}
            </section>
          </div>
        </section>
      </div>

      {editing && (
        <TemplateDialog
          template={editing.template}
          version={editing.version}
          busy={busy}
          onClose={() => setEditing(null)}
          onSave={async (values) => {
            const done = await save({ ...values, templateId: editing.template?.id });
            if (done) setEditing(null);
          }}
        />
      )}

      {removing && (
        <ConfirmDialog
          title="Excluir modelo"
          question={`"${removing.name}" sai da biblioteca e deixa de ser oferecido nas conversas. As mensagens já enviadas com ele continuam no histórico.`}
          confirmLabel="Excluir modelo"
          danger
          onConfirm={() => {
            void store
              .archiveMessageTemplate({ templateId: removing.id, archived: true })
              .then((result) => {
                if (result.ok) showFeedback("Modelo excluído da biblioteca.", "success");
              });
          }}
          onClose={() => setRemoving(null)}
        />
      )}
    </div>
  );
}

function TemplateDialog({
  template,
  version,
  busy,
  onClose,
  onSave,
}: {
  template?: MessageTemplate;
  version?: MessageTemplateVersion;
  busy: boolean;
  onClose: () => void;
  onSave: (values: { name: string; channel: CommunicationChannel; bodyText: string }) => void;
}) {
  const [name, setName] = useState(template?.name ?? "");
  const [channel, setChannel] = useState<CommunicationChannel>(template?.channel ?? "whatsapp");
  const [body, setBody] = useState(version?.bodyText ?? "");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const submit = () => {
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = "Dê um nome ao modelo.";
    if (!body.trim()) next.body = "Escreva o texto do modelo.";
    setErrors(next);
    if (Object.keys(next).length) return;
    onSave({ name: name.trim(), channel, bodyText: body.trim() });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      dirty={name !== (template?.name ?? "") || body !== (version?.bodyText ?? "")}
      title={template ? "Editar modelo" : "Novo modelo"}
      description="Use {{nome}} e {{empresa}} para personalizar o texto ao inseri-lo numa conversa. Salvar publica uma versão nova; as anteriores ficam no histórico."
      footer={
        <>
          <Button variant="text" onClick={onClose}>
            Cancelar
          </Button>
          <ToolbarSpacer />
          <Button variant="primary" loading={busy} onClick={submit}>
            Salvar modelo
          </Button>
        </>
      }
    >
      <div className="cx-form-grid">
        <Field label="Nome do modelo" required error={errors.name} className="cx-span-2">
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={name}
              placeholder="Ex.: Boas-vindas"
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </Field>
        <Field label="Canal" className="cx-span-2">
          {({ id }) => (
            <Select
              id={id}
              value={channel}
              // O canal define onde o modelo aparece; mudá-lo depois moveria o
              // texto de lugar sem avisar, então só a criação escolhe.
              disabled={Boolean(template)}
              onChange={(value) => setChannel(value as CommunicationChannel)}
              options={CHANNEL_ORDER.map((item) => ({
                value: item,
                label: CHANNEL_LABEL[item],
              }))}
            />
          )}
        </Field>
        <Field label="Mensagem" required error={errors.body} className="cx-span-2">
          {({ id, describedBy, invalid }) => (
            <TextArea
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              rows={6}
              value={body}
              maxLength={2000}
              placeholder="Olá, {{nome}}! …"
              onChange={(event) => setBody(event.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

export default ModelosPage;
